import { Layout, Layouts } from "react-grid-layout";
import { WidgetInstance } from "../components/DashboardWidgetConfig";
import { DashboardBreakpoint, LEFT_COLS } from "./generateLayouts";
import { WELCOME_BANNER_TYPE } from "../config/welcomeCampaign";

/**
 * Raw grid rows per breakpoint (left area: rowHeight 40px, margin 21px →
 * 4 rows = 223px, 6 rows = 345px). The banner needs more room than the
 * standard height unit (3 rows = 162px) because the German copy wraps to
 * several lines, especially in the narrow md/sm columns.
 */
export const WELCOME_BANNER_ROWS: Record<DashboardBreakpoint, number> = { lg: 4, md: 6, sm: 6 };

export const welcomeBannerLayoutItem = (bp: DashboardBreakpoint): Layout => ({
  i: WELCOME_BANNER_TYPE,
  x: 0,
  y: 0,
  w: Math.min(4, LEFT_COLS[bp]),
  h: WELCOME_BANNER_ROWS[bp],
});

/**
 * The banner is (re-)injected only when it is not on the board AND the user
 * has not dismissed the current campaign. Presence always wins: while the
 * instance exists, the dismissal stamp is irrelevant.
 */
export const shouldInjectWelcomeBanner = (
  leftInstances: WidgetInstance[],
  dismissedVersion: number | undefined,
  campaignVersion: number
): boolean =>
  !leftInstances.some(i => i.type === WELCOME_BANNER_TYPE) &&
  (dismissedVersion ?? 0) < campaignVersion;

/**
 * Prepend the banner at y:0 and shift every existing left-area item down by
 * the banner's height, per breakpoint. Because the banner spans the full
 * width at every breakpoint, a uniform y-shift is collision-free and keeps
 * the relative order; the grid's vertical compaction cleans up any slack.
 */
export const injectWelcomeBannerAtTop = (
  instances: WidgetInstance[],
  layouts: Layouts
): { instances: WidgetInstance[]; layouts: Layouts } => {
  const nextLayouts: Layouts = { lg: [], md: [], sm: [] };
  (Object.keys(WELCOME_BANNER_ROWS) as DashboardBreakpoint[]).forEach(bp => {
    const item = welcomeBannerLayoutItem(bp);
    nextLayouts[bp] = [item, ...(layouts[bp] ?? []).map(l => ({ ...l, y: l.y + item.h }))];
  });
  return {
    instances: [{ instanceId: WELCOME_BANNER_TYPE, type: WELCOME_BANNER_TYPE }, ...instances],
    layouts: nextLayouts,
  };
};
